import { Redirect } from "@-/com.npmjs.expo-router";
import { SettingsLayoutSkeleton } from "@-/dev.improve.haniverse.components.ui.suspense-page";
import { useAuthSession } from "@-/dev.improve.haniverse.lib.auth.client";
import { useSettingsIndexPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.settings.navigator";
import { useSignInSettingsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.settings.sign-in.navigator";
import type { PropsWithChildren } from "react";
import { useShowAdminSettingsSection } from "#use-show-admin-settings-section";

/**
 * Wraps every `settings/admin/*` route. Hiding the Admin section only removes the entry point:
 * the routes themselves stay registered, so a deep link (`haniverse://settings/admin/…`) would
 * otherwise open the admin tools for any user. Non-admins are sent back to Settings instead.
 *
 * It uses the same check as the section, and decides only once the session has resolved: on web
 * the first render precedes `get-session`, which would otherwise send an admin's deep link back to
 * Settings.
 *
 * `requiresSession` is for tools backed by admin RPCs. Development builds show the Admin section
 * to everyone, so a signed-out visitor would otherwise fire those RPCs and land on "Session
 * expired" from their UNAUTHORIZED response; they are sent to Sign In instead.
 */
export function AdminRouteGate({
  children,
  requiresSession = false,
}: PropsWithChildren<{ requiresSession?: boolean }>) {
  const session = useAuthSession();
  const showAdminSettings = useShowAdminSettingsSection();
  const settingsIndexPageNavigator = useSettingsIndexPageNavigator();
  const signInSettingsPageNavigator = useSignInSettingsPageNavigator();

  if ((requiresSession || !__DEV__) && !session.isResolved) {
    return <SettingsLayoutSkeleton />;
  }
  if (!showAdminSettings) {
    return <Redirect href={settingsIndexPageNavigator.resolveHref()} />;
  }
  if (requiresSession && session.user === null) {
    return <Redirect href={signInSettingsPageNavigator.resolveHref()} />;
  }
  return children;
}
